/**
 * @file theme.ts
 * @description Centralized design-token configuration.
 *
 * This is the SINGLE SOURCE OF TRUTH for all colours, radii, spacing and
 * semantic variants used across the admin panel.
 *
 * CSS custom-properties are declared in `src/index.css`.
 * Components import from here — never use hardcoded colour literals.
 */

// ─── CSS Variable Helpers ────────────────────────────────────────────────────

/** Returns a CSS `hsl()` call that reads from the CSS variable. */
export const cssVar = (variable: string) => `hsl(var(${variable}))`;

// ─── Brand Palette ───────────────────────────────────────────────────────────

export const BRAND_COLORS = {
  /** Justice Navy – primary brand colour */
  primary: cssVar('--primary'),
  primaryForeground: cssVar('--primary-foreground'),

  /** Justice Gold – secondary / accent colour */
  secondary: cssVar('--secondary'),
  secondaryForeground: cssVar('--secondary-foreground'),

  background: cssVar('--background'),
  foreground: cssVar('--foreground'),

  card: cssVar('--card'),
  cardForeground: cssVar('--card-foreground'),

  muted: cssVar('--muted'),
  mutedForeground: cssVar('--muted-foreground'),

  accent: cssVar('--accent'),
  accentForeground: cssVar('--accent-foreground'),

  border: cssVar('--border'),
  input: cssVar('--input'),
  ring: cssVar('--ring'),
} as const;

// ─── Semantic Colours ────────────────────────────────────────────────────────

export const SEMANTIC_COLORS = {
  success: cssVar('--success'),
  successForeground: cssVar('--success-foreground'),

  warning: cssVar('--warning'),
  warningForeground: cssVar('--warning-foreground'),

  destructive: cssVar('--destructive'),
  destructiveForeground: cssVar('--destructive-foreground'),
} as const;

// ─── Sidebar Tokens ──────────────────────────────────────────────────────────

export const SIDEBAR_COLORS = {
  background: cssVar('--sidebar-background'),
  foreground: cssVar('--sidebar-foreground'),
  muted: cssVar('--sidebar-muted'),
  primary: cssVar('--sidebar-primary'),
  primaryForeground: cssVar('--sidebar-primary-foreground'),
  accent: cssVar('--sidebar-accent'),
  accentForeground: cssVar('--sidebar-accent-foreground'),
  border: cssVar('--sidebar-border'),
  ring: cssVar('--sidebar-ring'),
} as const;

// ─── Border Radius Scale ─────────────────────────────────────────────────────

export const RADIUS = {
  DEFAULT: 'var(--radius)', // 0.75rem
  '2xl': 'var(--radius-2xl)',
  xl: 'var(--radius-xl)',
  lg: 'var(--radius-lg)',
  md: 'var(--radius-md)',
  sm: 'var(--radius-sm)',
} as const;

// ─── Layout Dimensions ───────────────────────────────────────────────────────

export const LAYOUT = {
  sidebarWidth: 'var(--sidebar-width)',
  sidebarCollapsedWidth: 'var(--sidebar-collapsed-width)',
  headerHeight: 'var(--header-height)',
} as const;

// ─── Badge Variant Maps ──────────────────────────────────────────────────────

/**
 * Maps a ticket/entity status string to the corresponding Badge variant.
 * Import and use in any page that renders status badges.
 */
export const STATUS_BADGE_VARIANT_MAP: Record<
  string,
  'info' | 'warning' | 'success' | 'secondary' | 'destructive' | 'default' | 'outline' | 'premium'
> = {
  // Ticket statuses
  OPEN: 'info',
  IN_PROGRESS: 'warning',
  RESOLVED: 'success',
  CLOSED: 'secondary',

  // Priority
  HIGH: 'destructive',
  MEDIUM: 'warning',
  LOW: 'info',

  // Generic
  ACTIVE: 'success',
  ACCEPTED: 'success',
  INACTIVE: 'secondary',
  PENDING: 'warning',
  APPROVED: 'success',
  COMPLETE: 'success',
  REJECTED: 'destructive',
  BLOCKED: 'destructive',
  DRAFT: 'outline',
} as const;

export type BadgeVariantKey = keyof typeof STATUS_BADGE_VARIANT_MAP;

// ─── Tailwind Class Utilities ────────────────────────────────────────────────

/**
 * Shared container classes for page wrappers.
 * Use `PAGE_CLASSES.root` on the outermost div of every page component.
 */
export const PAGE_CLASSES = {
  root: 'p-6 space-y-6 animate-fade-in',
  header: 'flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between',
  title: 'text-2xl font-bold tracking-tight',
  subtitle: 'text-sm text-muted-foreground',
} as const;

/**
 * Standard icon dimensions used in action buttons and cells.
 */
export const ICON_SIZE = {
  xs: 'h-3 w-3',
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
} as const;

// ─── App Meta ────────────────────────────────────────────────────────────────

export const APP_META = {
  name: 'Admin Panel',
  description: 'Centralized administration dashboard',
  version: '1.0.0',
} as const;
